/** Pack-ticket helpers for frontend display and forms (single / couple / group). */

export const COUPLE_PERSONS_PER_UNIT = 2;
export const DEFAULT_GROUP_PERSONS_PER_UNIT = 4;

export function personsPerUnit(ticket = {}) {
  const explicit = Number(ticket?.personsPerUnit);
  if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
  const kind = String(ticket?.ticketKind || "").toLowerCase();
  if (kind === "group" || ticket?.isGroupTicket) return DEFAULT_GROUP_PERSONS_PER_UNIT;
  if (kind === "couple" || ticket?.isCoupleTicket) return COUPLE_PERSONS_PER_UNIT;
  return 1;
}

export function isCoupleTicket(ticket = {}) {
  const kind = String(ticket?.ticketKind || "").toLowerCase();
  if (kind === "couple") return true;
  if (kind === "group") return false;
  return Boolean(ticket?.isCoupleTicket) && !ticket?.isGroupTicket;
}

export function isGroupTicket(ticket = {}) {
  const kind = String(ticket?.ticketKind || "").toLowerCase();
  if (kind === "group") return true;
  return Boolean(ticket?.isGroupTicket);
}

export function isMultiPersonPack(ticket = {}) {
  return personsPerUnit(ticket) > 1;
}

export function totalPersons(qty, ticket = {}) {
  const n = Math.max(0, Number(qty) || 0);
  return n * personsPerUnit(ticket);
}

export function coupleTicketBadge(ticket = {}) {
  const ppu = personsPerUnit(ticket);
  if (ppu <= 1) return null;
  if (isGroupTicket(ticket) || ppu > 2) return `Group · ${ppu} persons`;
  return `Couple · ${ppu} persons`;
}

export function coupleQuantityLabel(qty, ticket = {}) {
  const n = Math.max(0, Number(qty) || 0);
  const ppu = personsPerUnit(ticket);
  if (ppu <= 1) {
    return n === 1 ? "1 ticket" : `${n} tickets`;
  }
  const persons = totalPersons(n, ticket);
  if (isGroupTicket(ticket) || ppu > 2) {
    return n === 1
      ? `1 group ticket (${persons} persons)`
      : `${n} group tickets (${persons} persons)`;
  }
  return n === 1
    ? `1 couple ticket (${persons} persons)`
    : `${n} couple tickets (${persons} persons)`;
}

export function suggestCoupleTicketName(baseName = "") {
  const trimmed = String(baseName || "").trim().replace(/^(couple|group)\s+/i, "").trim();
  if (!trimmed) return "Couple ";
  if (/^couple\b/i.test(trimmed)) return trimmed;
  return `Couple ${trimmed}`;
}

export function suggestGroupTicketName(baseName = "") {
  const trimmed = String(baseName || "").trim().replace(/^(couple|group)\s+/i, "").trim();
  if (!trimmed) return "Group ";
  if (/^group\b/i.test(trimmed)) return trimmed;
  return `Group ${trimmed}`;
}

export function regularTicketName(baseName = "") {
  return String(baseName || "")
    .trim()
    .replace(/^(couple|group)\s+/i, "")
    .trim();
}

export function ticketTierLabel(ticket = {}, displayNameFn = (v) => v) {
  const raw = ticket.type || ticket.categoryId || ticket.tier || "";
  const base = displayNameFn(raw) || String(raw || "").trim();
  if (isGroupTicket(ticket)) {
    return /^group\b/i.test(base) ? base : suggestGroupTicketName(base);
  }
  if (isCoupleTicket(ticket)) {
    return /^couple\b/i.test(base) ? base : suggestCoupleTicketName(base);
  }
  return regularTicketName(base) || base;
}

export function ticketSelectionKey(ticket = {}, index = 0) {
  const categoryId = String(ticket.type ?? ticket.categoryId ?? ticket.key ?? "").trim();
  const kind = isGroupTicket(ticket)
    ? "group"
    : isCoupleTicket(ticket)
      ? "couple"
      : "single";
  return `${categoryId}::${index}::${kind}`;
}

export function ticketCategoryId(ticket = {}) {
  return String(ticket.type ?? ticket.categoryId ?? ticket.key ?? "").trim();
}

export function isAllDaysTicket(ticket = {}, event = null) {
  // Explicit day binding always wins
  if (String(ticket?.date || "").trim()) return false;
  if (String(ticket?.validFor || "").trim() === "all_days") return true;
  if (String(ticket?.validFor || "").trim() === "one_day") return false;
  // Legacy: undated categories on an all_days event
  if (String(event?.ticketPricingMode || "") === "all_days") {
    return true;
  }
  return false;
}

/**
 * Sale window check (inclusive). Empty saleStart/saleEnd = always on sale.
 * saleEnd as YYYY-MM-DD is treated as end of that IST day.
 */
export function isTicketSaleActive(ticket = {}, now = new Date()) {
  if (ticket?.isTicketAvailable === false) return false;
  const t = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(t.getTime())) return true;

  if (ticket?.saleStart) {
    const start = parseSaleBoundary(ticket.saleStart, "start");
    if (start && t < start) return false;
  }
  if (ticket?.saleEnd) {
    const end = parseSaleBoundary(ticket.saleEnd, "end");
    if (end && t > end) return false;
  }
  return true;
}

function parseSaleBoundary(value, edge) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const ymd = value.trim();
    return edge === "end"
      ? new Date(`${ymd}T23:59:59.999+05:30`)
      : new Date(`${ymd}T00:00:00.000+05:30`);
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** datetime-local / date input helpers */
export function toDateInputValue(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  // Use IST calendar day for display
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
}

export function saleDateToIso(dateStr, edge = "start") {
  const ymd = String(dateStr || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return edge === "end"
    ? new Date(`${ymd}T23:59:59.999+05:30`)
    : new Date(`${ymd}T00:00:00.000+05:30`);
}
